import { useCallback, useEffect, useMemo, useRef } from "react"
import { useSearchParams } from "react-router"

/**
 * Syncs view-level params (segments, filters, dates, etc.) to URL search params.
 *
 * Each key in `defaults` becomes a URL param. Strings are stored as-is;
 * arrays/objects are JSON-serialized. All params are always present in the URL.
 *
 * Pass a **stable** `defaults` object (module-level const) to avoid
 * unnecessary recomputations.
 */
export function useViewParams<T extends Record<string, unknown>>(defaults: T) {
  const [searchParams, setSearchParams] = useSearchParams()
  const defaultsRef = useRef(defaults)

  // Write any missing default params into the URL on mount
  const initialized = useRef(false)
  useEffect(() => {
    if (initialized.current) return
    initialized.current = true
    const missing: [string, string][] = []
    for (const key of Object.keys(defaultsRef.current)) {
      if (!searchParams.has(key)) {
        const val = defaultsRef.current[key]
        missing.push([key, typeof val === "string" ? val : JSON.stringify(val)])
      }
    }
    if (missing.length > 0) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of missing) next.set(k, v)
        return next
      }, { replace: true })
    }
  }, [searchParams, setSearchParams])

  // Cache the parsed value per key by its raw URL string. JSON.parse always
  // returns a fresh reference, so without this cache every URL change makes
  // params.filterX a new array even when the content is unchanged — which
  // invalidates downstream `useMemo` deps and forces consumers like
  // RetailerSupplyView to re-walk their derived-data graphs against stale data
  // before the new fetch even fires. Reusing the prior reference when raw is
  // unchanged keeps those caches hot.
  const parsedCacheRef = useRef<Record<string, { raw: string; parsed: unknown }>>({})
  const params = useMemo<T>(() => {
    const result = {} as Record<string, unknown>
    const cache = parsedCacheRef.current
    for (const key of Object.keys(defaultsRef.current)) {
      const raw = searchParams.get(key)
      if (raw === null) {
        result[key] = defaultsRef.current[key]
        continue
      }
      if (typeof defaultsRef.current[key] === "string") {
        result[key] = raw
        continue
      }
      const cached = cache[key]
      if (cached && cached.raw === raw) {
        result[key] = cached.parsed
        continue
      }
      let parsed: unknown
      try { parsed = JSON.parse(raw) }
      catch { parsed = defaultsRef.current[key] }
      cache[key] = { raw, parsed }
      result[key] = parsed
    }
    return result as T
  }, [searchParams])

  const setParam = useCallback(<K extends keyof T & string>(key: K, value: T[K]) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set(key, typeof value === "string" ? value : JSON.stringify(value))
      return next
    }, { replace: true })
  }, [setSearchParams])

  // Atomic multi-param update. Use when two params must change together to
  // avoid an intermediate render where the data layer fires a request with
  // mismatched params (e.g. new grain + stale filter values from old grain).
  const setParams = useCallback((updates: Partial<T>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      for (const key of Object.keys(updates) as (keyof T & string)[]) {
        const value = updates[key]
        if (value === undefined) continue
        next.set(key, typeof value === "string" ? value : JSON.stringify(value))
      }
      return next
    }, { replace: true })
  }, [setSearchParams])

  return { params, setParam, setParams } as const
}
